/**
 * SOLID Review
 * S — Strong. One concern: whether a signed-in player must re-accept the legal terms, which documents, and
 *     which consents accepting them records — ConsentReacceptanceGate's inline rules, moved.
 * O — Good. Gating on a third document kind is one more literal in `REVIEWED`.
 * L — N/A — functions.
 * I — Good. Three decisions on the one ClientDecisions contract.
 * D — Strong. The decision helpers and the port schemas only.
 */
import { decided, type IEntityDecision, type TDecisionResult } from '../shared';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type {
  ICommandPlanLegalReview, IEntityPlanLegalReview, ICommandLegalReviewOf, IEntityLegalReview, ICommandLegalAcceptance,
  IEntityLegalAcceptance, IPayloadLegalDocument, IPayloadRecordConsent,
} from '../../ports/schemas/LegalMoves';

/**
 * THE RE-ACCEPTANCE GATE'S RULES (frontend strict-ports plan, FE4a, 2026-10-04). The gate blocked a
 * signed-in player whose recorded consent no longer matched the current Terms or Privacy Policy, until they
 * accepted again. Moved, not rewritten: it engages ONLY on an explicit "not all current" — any other answer
 * fails open — and only when there is a Terms or Privacy document to show; accepting records Terms, then
 * Privacy, at the versions shown. None of these change state: the gate's own dialog is its business.
 */

type TDecided<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const NOTHING: IPropsClientChanges = {};
/** The two documents a player re-accepts, in the order they are shown and recorded. */
const REVIEWED: ReadonlyArray<string> = ['TERMS_OF_SERVICE', 'PRIVACY_POLICY'];

/** The consent status: fetch the documents only when the server explicitly says a consent is stale. */
export function decidePlanLegalReview(_input: IPropsClientInput, command: ICommandPlanLegalReview): TDecided<IEntityPlanLegalReview> {
  const plan: IEntityPlanLegalReview = { fetchDocuments: command.status.allCurrent === false };
  return decided(NOTHING, plan);
}

function reviewed(documents: ReadonlyArray<IPayloadLegalDocument>): IPayloadLegalDocument[] {
  const shown: IPayloadLegalDocument[] = [];
  for (const kind of REVIEWED) {
    const document = documents.find((d) => d.type === kind);
    if (document !== undefined) shown.push(document);
  }
  return shown;
}

/** The documents to re-accept: Terms, then Privacy, whichever exist — none, and the gate stays closed. */
export function decideLegalReviewOf(_input: IPropsClientInput, command: ICommandLegalReviewOf): TDecided<IEntityLegalReview> {
  const review: IEntityLegalReview = { review: reviewed(command.documents) };
  return decided(NOTHING, review);
}

/** Accepting: one consent per reviewed document, at the version shown, Terms first. */
export function decidePlanLegalAcceptance(_input: IPropsClientInput, command: ICommandLegalAcceptance): TDecided<IEntityLegalAcceptance> {
  const consents = reviewed(command.review).map((document): IPayloadRecordConsent => ({ documentType: document.type, documentVersion: document.version }));
  const plan: IEntityLegalAcceptance = { consents };
  return decided(NOTHING, plan);
}
